import type { ReactNode } from 'react'
import Link from 'next/link'
import { ImageIcon } from 'lucide-react'

import { cn } from '@/lib/utils'
import { Card, CardContent } from '@/components/ui/card'

export type ContentCardProps = {
  title: string
  /** Makes the whole card a link (and applies the interactive hover lift). */
  href?: string
  /** Small meta line above the title (date, category…). */
  eyebrow?: ReactNode
  /** A `Tag` (type / points / category), rendered above the title (or over the image when featured). */
  badge?: ReactNode
  description?: ReactNode
  /** CTA / actions row below the description. */
  footer?: ReactNode
  /** Custom media (image or logo). Falls back to a labelled placeholder. */
  media?: ReactNode
  mediaLabel?: string
  /** `cover` for photos (default); `contain` pads a brand logo on a cream field. */
  mediaFit?: 'cover' | 'contain'
  /** Large, image-dominant card with overlaid text (e.g. "Partner of the Month"). */
  featured?: boolean
  className?: string
}

function MediaBox({
  media,
  label,
  fit = 'cover',
  className,
}: {
  media?: ReactNode
  label?: string
  fit?: 'cover' | 'contain'
  className?: string
}) {
  if (media) {
    return (
      <div
        className={cn(
          'bg-muted overflow-hidden',
          fit === 'contain' && 'flex items-center justify-center bg-brand-cream p-6',
          className,
        )}
      >
        {media}
      </div>
    )
  }
  return (
    <div
      className={cn(
        'bg-muted text-muted-foreground flex flex-col items-center justify-center gap-1.5',
        className,
      )}
    >
      <ImageIcon className="size-6" />
      {label && <span className="px-2 text-center text-sm font-medium">{label}</span>}
    </div>
  )
}

export function ContentCard({
  title,
  href,
  eyebrow,
  badge,
  description,
  footer,
  media,
  mediaLabel,
  mediaFit = 'cover',
  featured = false,
  className,
}: ContentCardProps) {
  const card = featured ? (
    <Card interactive={!!href} className={cn('relative min-h-72 overflow-hidden p-0', className)}>
      <MediaBox media={media} label={mediaLabel} fit={mediaFit} className="absolute inset-0 size-full" />
      <div className="media-overlay absolute inset-0" />
      <div className="absolute inset-x-0 bottom-0 flex flex-col gap-1 p-card-compact text-brand-cream [&_h3]:text-brand-cream [&_h4]:text-brand-cream">
        {badge && <div className="mb-1">{badge}</div>}
        {eyebrow && <div className="text-sm text-brand-cream/80">{eyebrow}</div>}
        <h3>{title}</h3>
        {description && <p className="line-clamp-2 text-sm text-brand-cream/90">{description}</p>}
        {footer && <div className="mt-4">{footer}</div>}
      </div>
    </Card>
  ) : (
    <Card interactive={!!href} className={cn('h-full gap-0 overflow-hidden py-0', className)}>
      <MediaBox media={media} label={mediaLabel} fit={mediaFit} className="aspect-video w-full" />
      <CardContent className="flex flex-1 flex-col justify-between gap-6 py-card-snug">
        <div className="min-w-0">
          {eyebrow && <div className="text-muted-foreground mb-1 text-sm">{eyebrow}</div>}
          {badge && <div className="mb-1">{badge}</div>}
          <h3>{title}</h3>
          {description && (
            <p className="text-muted-foreground mt-1 line-clamp-2 text-base">{description}</p>
          )}
        </div>
        {footer && <div className="w-full [&>*]:w-full">{footer}</div>}
      </CardContent>
    </Card>
  )

  if (href) {
    return (
      <Link href={href} className="block h-full">
        {card}
      </Link>
    )
  }
  return card
}
